'use client';

import { useMediaQuery } from '@chakra-ui/react/hooks';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

import breakpoints from '../theme/breakpoints';

export interface ResponsiveState {
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isWideDesktop: boolean;
}

// Tiers are expressed with rem min-widths only — the same unit as the theme
// breakpoints — then combined into mutually exclusive flags. Pairing a rem
// min-width with a px max-width would drift apart when the browser's default
// font size isn't 16px (rem media queries scale with it), leaving gaps. Pure
// min-width comparisons keep the ranges gap-free at any font size, so exactly
// one flag is true at any width.
//
// The leading `min-width: 0rem` query always matches once evaluated on the
// client, so it doubles as a "measured" sentinel (false during SSR/first paint).
const mediaQueries = [
  `(min-width: ${breakpoints.base})`,
  `(min-width: ${breakpoints.tablet})`,
  `(min-width: ${breakpoints.desktop})`,
  `(min-width: ${breakpoints.wideDesktop})`,
];

const defaultState: ResponsiveState = {
  isDesktop: false,
  isMobile: false,
  isTablet: false,
  isWideDesktop: false,
};

const ResponsiveContext = createContext<ResponsiveState>(defaultState);

export const ResponsiveProvider = ({ children }: { children: ReactNode }) => {
  const [measured, aboveTablet, aboveDesktop, aboveWideDesktop] = useMediaQuery(mediaQueries);

  const value = useMemo<ResponsiveState>(() => {
    // `measured` is false during SSR/first render and true once the queries are
    // evaluated client-side. Until then report all flags false (matching v1) so
    // SSR/first paint stays on the desktop branch of the common
    // `isMobile ? … : …` pattern — our desktop-majority audience never flashes
    // the mobile layout before hydration.
    if (!measured) return defaultState;
    return {
      isDesktop: aboveDesktop && !aboveWideDesktop,
      isMobile: !aboveTablet,
      isTablet: aboveTablet && !aboveDesktop,
      isWideDesktop: aboveWideDesktop,
    };
  }, [measured, aboveTablet, aboveDesktop, aboveWideDesktop]);

  return <ResponsiveContext.Provider value={value}>{children}</ResponsiveContext.Provider>;
};

ResponsiveProvider.displayName = 'ResponsiveProvider';

const useResponsive = (): ResponsiveState => useContext(ResponsiveContext);

export default useResponsive;
